import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { collection, query, where, orderBy, onSnapshot, addDoc, updateDoc, deleteDoc, doc, getDocs } from 'firebase/firestore';
import { db } from './firebase';
import { Calendar, Plus, ChevronLeft, ChevronRight, Eye, EyeOff, Send, Zap, AlertTriangle, Building2, UserPlus, X as XIcon, DollarSign, Shield, Trash2, Edit3, Sparkles, Check, Loader2 } from 'lucide-react';
import { getWeekStart, getWeekDates, validateShiftAssignment, DEFAULT_SCHEDULING_CONFIG, VIEW_RANGES, getDateRange, chunkIntoWeeks, navigateRange, validateBlockAssignment, getSectorConfig } from './utils/schedulingEngine';
import { generateAutoFillSuggestions, generateEnhancedSuggestions } from './utils/aiInsights';
import { logAuditEvent, AUDIT_ACTIONS } from './utils/auditLog';
import { sendNotification, NOTIFICATION_TYPES } from './utils/notificationUtils';
import { isManager, hasPermission } from './utils/permissions';
import { SEGMENT_TYPES, BLOCK_TYPES, getBlockWorkingHours, getBlockTimeSpan } from './utils/shiftTypes';
import { projectRotaCost } from './utils/payRules';
import { getRotaFatigueWarnings } from './utils/fatigueEngine';
import { analysePatterns, generateSmartRota, summariseSmartRota } from './utils/smartRotaBuilder';
import { canAccessGroup, filterGroupsByAccess, filterShiftsByAccess, filterStaffByAccess } from './utils/locationAccess';
import ShiftBlockModal from './components/ShiftBlockModal';
import ComplianceOverrideModal from './components/ComplianceOverrideModal';
import LoadingSpinner from './components/LoadingSpinner';

const DAY_NAME_KEY_BY_INDEX = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

const getStaffAssignmentId = (staffMember) => (
  staffMember?.uid ||
  staffMember?.userId ||
  staffMember?.authUid ||
  staffMember?.staffId ||
  staffMember?.id ||
  null
);

const mapSettingsToSchedulingConfig = (shiftsSettings = {}) => ({
  ...DEFAULT_SCHEDULING_CONFIG,
  ...(shiftsSettings.scheduling || {}),
  minRestHoursBetweenShifts: shiftsSettings.minRestHours ?? shiftsSettings.scheduling?.minRestHoursBetweenShifts ?? DEFAULT_SCHEDULING_CONFIG.minRestHoursBetweenShifts,
  maxWeeklyHours: shiftsSettings.maxWeeklyHours ?? shiftsSettings.scheduling?.maxWeeklyHours ?? DEFAULT_SCHEDULING_CONFIG.maxWeeklyHours,
  maxDailyHours: shiftsSettings.maxDailyHours ?? shiftsSettings.scheduling?.maxDailyHours ?? DEFAULT_SCHEDULING_CONFIG.maxDailyHours,
  maxConsecutiveDays: shiftsSettings.maxConsecutiveDays ?? shiftsSettings.scheduling?.maxConsecutiveDays ?? DEFAULT_SCHEDULING_CONFIG.maxConsecutiveDays,
});

const hexToRgba = (hex, alpha) => {
  if (!hex) return null;
  const normalized = hex.replace('#', '').trim();
  if (![3, 6].includes(normalized.length)) return null;

  const fullHex = normalized.length === 3
    ? normalized.split('').map((char) => `${char}${char}`).join('')
    : normalized;

  const value = Number.parseInt(fullHex, 16);
  if (Number.isNaN(value)) return null;

  const r = (value >> 16) & 255;
  const g = (value >> 8) & 255;
  const b = value & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const getDominantDayColor = (dayShifts = [], getShiftColor = () => null) => {
  const counts = dayShifts.reduce((acc, shift) => {
    const color = getShiftColor(shift);
    if (!color) return acc;
    acc[color] = (acc[color] || 0) + 1;
    return acc;
  }, {});

  return Object.entries(counts)
    .sort((a, b) => b[1] - a[1])
    .map(([color]) => color)[0] || null;
};

const calculateShiftSummaryMetrics = (shift) => {
  if (shift.segments?.length > 0) {
    return {
      workingHours: getBlockWorkingHours(shift.segments, false) || 0,
      totalHours: getBlockWorkingHours(shift.segments, true) || 0,
      sleepInCount: shift.segments.filter((segment) => segment.type === SEGMENT_TYPES.SLEEP_IN).length,
    };
  }

  const span = getBlockTimeSpan([{ startTime: shift.startTime, endTime: shift.endTime, type: SEGMENT_TYPES.STANDARD }]);
  return {
    workingHours: span.spanHours || 0,
    totalHours: span.spanHours || 0,
    sleepInCount: shift.hasSleepIn ? 1 : 0,
  };
};

const resolveStaffDisplayName = (shift, staffMember) => (
  staffMember?.displayName ||
  staffMember?.fullName ||
  staffMember?.name ||
  staffMember?.username ||
  (staffMember?.email ? staffMember.email.split('@')[0] : '') ||
  shift?.assignedStaffName ||
  shift?.assignedToName ||
  shift?.staffName ||
  shift?.assignedStaffId ||
  ''
);

export default function RotaManager({ user, userData, companyId }) {
  const { t } = useTranslation();
  const [weekStartDay, setWeekStartDay] = useState(1);
  const [weekStart, setWeekStart] = useState(getWeekStart(new Date().toISOString().split('T')[0], 1));
  const [viewRange, setViewRange] = useState('1w');
  const [shifts, setShifts] = useState([]);
  const [rota, setRota] = useState(null);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateShift, setShowCreateShift] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState('all');
  const [aiSuggestions, setAiSuggestions] = useState({});
  // eslint-disable-next-line no-unused-vars
  const [coverageGaps, setCoverageGaps] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [aiFilters, setAiFilters] = useState({});
  // eslint-disable-next-line no-unused-vars
  const [config, setConfig] = useState(DEFAULT_SCHEDULING_CONFIG);
  const [editingShift, setEditingShift] = useState(null);
  const [costProjection, setCostProjection] = useState(null);
  const [showCostPanel, setShowCostPanel] = useState(false);
  const [fatigueWarnings, setFatigueWarnings] = useState([]);
  const [overrideModal, setOverrideModal] = useState(null);
  const [sectorConfig, setSectorConfig] = useState(null);
  const [payRules, setPayRules] = useState(null);
  const [shiftTemplates, setShiftTemplates] = useState([]);
  const [smartRotaDrafts, setSmartRotaDrafts] = useState(null);
  const [smartRotaSummary, setSmartRotaSummary] = useState(null);
  const [smartRotaLoading, setSmartRotaLoading] = useState(false);
  const [smartRotaOpen, setSmartRotaOpen] = useState(false);
  const [showPublishReview, setShowPublishReview] = useState(false);

  const weekDates = getWeekDates(weekStart);
  const allDates = getDateRange(weekStart, viewRange);
  const weekChunks = chunkIntoWeeks(allDates);
  const parseDisplayDate = (date) => new Date(`${date}T00:00:00`);
  const dayNameForDate = (date) => t(`rota.${DAY_NAME_KEY_BY_INDEX[parseDisplayDate(date).getDay()]}`);
  const accessibleGroups = useMemo(() => filterGroupsByAccess(groups, userData), [groups, userData]);
  const accessibleGroupsById = useMemo(() => (
    accessibleGroups.reduce((acc, group) => {
      acc[group.id] = group;
      return acc;
    }, {})
  ), [accessibleGroups]);
  const accessibleStaff = useMemo(() => filterStaffByAccess(staff, userData, user?.uid), [staff, userData, user?.uid]);
  const accessibleShifts = useMemo(() => filterShiftsByAccess(shifts, userData), [shifts, userData]);
  const staffByAssignmentId = useMemo(() => {
    const map = {};
    accessibleStaff.forEach((member) => {
      if (member.assignmentId) map[member.assignmentId] = member;
      map[member.id] = member;
      if (member.uid) map[member.uid] = member;
      if (member.userId) map[member.userId] = member;
      if (member.authUid) map[member.authUid] = member;
      if (member.staffId) map[member.staffId] = member;
    });
    return map;
  }, [accessibleStaff]);

  useEffect(() => {
    if (selectedGroup === 'all') return;
    if (!accessibleGroups.some((group) => group.id === selectedGroup)) {
      setSelectedGroup(accessibleGroups[0]?.id || 'all');
    }
  }, [accessibleGroups, selectedGroup]);

  useEffect(() => {
    if (!companyId) return;

    // Subscribe to shifts for the visible date range
    const shiftsQuery = query(
      collection(db, 'companies', companyId, 'shifts'),
      where('date', '>=', allDates[0]),
      where('date', '<=', allDates[allDates.length - 1]),
      orderBy('date'),
      orderBy('startTime')
    );

    const unsubShifts = onSnapshot(shiftsQuery, (snapshot) => {
      setShifts(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, (error) => {
      console.error('Error listening to shifts:', error);
      setLoading(false);
    });

    // Subscribe to rota document
    const rotaQuery = query(
      collection(db, 'companies', companyId, 'rotas'),
      where('weekStart', '==', weekStart)
    );

    const unsubRota = onSnapshot(rotaQuery, (snapshot) => {
      if (!snapshot.empty) {
        const rotaDoc = snapshot.docs[0];
        setRota({ id: rotaDoc.id, ...rotaDoc.data() });
      } else {
        setRota(null);
      }
    }, (error) => {
      console.error('Error listening to rotas:', error);
    });

    // Subscribe to staff (real-time so new staff appear immediately)
    const unsubStaff = onSnapshot(
      query(collection(db, 'companies', companyId, 'users'), where('active', '!=', false)),
      (snapshot) => {
        setStaff(snapshot.docs.map(d => {
          const data = d.data();
          const normalizedName =
            data.displayName ||
            data.fullName ||
            data.name ||
            data.username ||
            (data.email ? data.email.split('@')[0] : '') ||
            d.id;
          return {
            id: d.id,
            ...data,
            displayName: normalizedName,
            assignmentId: getStaffAssignmentId({ id: d.id, ...data }),
          };
        }));
      },
      (error) => console.error('Error listening to staff:', error)
    );

    // Subscribe to groups/locations
    const unsubGroups = onSnapshot(
      collection(db, 'companies', companyId, 'shiftsGroups'),
      (snapshot) => {
        setGroups(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
      },
      (error) => {
        console.error('Error listening to groups:', error);
      }
    );

    // Subscribe to shift templates
    const unsubTemplates = onSnapshot(
      collection(db, 'companies', companyId, 'shiftTemplates'),
      snap => setShiftTemplates(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
      error => console.error('Error listening to templates:', error)
    );

    // Subscribe to company settings (for sector config + pay rules)
    const unsubSettings = onSnapshot(
      doc(db, 'companies', companyId),
      (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          if (data.shiftsSettings) {
            const nextWeekStartDay = data.shiftsSettings.weekStartDay ?? data.shiftsSettings.scheduling?.weekStartDay ?? 1;
            setWeekStartDay(nextWeekStartDay);
            setWeekStart(prev => getWeekStart(prev || new Date().toISOString().split('T')[0], nextWeekStartDay));
            if (data.shiftsSettings.sectorConfig) setSectorConfig(data.shiftsSettings.sectorConfig);
            if (data.shiftsSettings.payRules) setPayRules(data.shiftsSettings.payRules);
            setConfig(mapSettingsToSchedulingConfig(data.shiftsSettings));
          }
        }
      },
      error => console.error('Error listening to settings:', error)
    );

    return () => {
      unsubShifts();
      unsubRota();
      unsubStaff();
      unsubGroups();
      unsubTemplates();
      unsubSettings();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, weekStart, viewRange]);

  const navigateView = (direction) => {
    setWeekStart(navigateRange(weekStart, direction, viewRange));
  };

  const createShift = async (shiftData) => {
    try {
      if (shiftData.groupId && !canAccessGroup(userData, shiftData.groupId)) {
        window.alert(t('rota.locationAccessDenied', 'You do not have access to this location.'));
        return;
      }

      const assignedMember = accessibleStaff.find(s => s.assignmentId === shiftData.assignedStaffId || s.id === shiftData.assignedStaffId);
      const assignedStaffId = shiftData.assignedStaffId
        ? getStaffAssignmentId(assignedMember || { id: shiftData.assignedStaffId })
        : null;
      const assignedStaffName = resolveStaffDisplayName(shiftData, assignedMember);
      const newShift = {
        ...shiftData,
        assignedStaffId,
        assignedTo: assignedStaffId,
        assignedStaffName,
        assignedToName: assignedStaffName,
        companyId,
        rotaId: rota?.id || null,
        status: assignedStaffId ? 'assigned' : 'unassigned',
        createdBy: user.uid,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      // Validate if assigning staff (block-aware), but do not block saving.
      // Rota settings can still surface compliance issues without preventing the shift being created.
      if (newShift.assignedStaffId) {
        const validation = newShift.segments?.length > 0
          ? validateBlockAssignment(newShift.assignedStaffId, newShift, accessibleShifts, {}, config)
          : validateShiftAssignment(newShift.assignedStaffId, newShift, accessibleShifts, {}, config);

        if (!validation.valid) {
          newShift.complianceOverride = {
            reason: 'Saved from rota despite compliance validation issues',
            by: user.uid,
            byName: userData?.displayName || user.email || 'Unknown user',
            at: new Date().toISOString(),
            errors: validation.errors || [],
            warnings: validation.warnings || [],
            automatic: true,
          };
        } else if (validation.warnings?.length) {
          newShift.complianceWarnings = validation.warnings;
        }
      }

      if (editingShift?.id) {
        // Update existing shift
        const { id, ...updateData } = newShift;
        await updateDoc(doc(db, 'companies', companyId, 'shifts', editingShift.id), {
          ...updateData,
          updatedAt: new Date().toISOString(),
        });
        await logAuditEvent(companyId, AUDIT_ACTIONS.SHIFT_BLOCK_UPDATED, {
          performedBy: user.uid,
          performedByName: userData?.displayName,
          targetId: editingShift.id,
          targetType: 'shift',
          newValue: updateData,
        });
      } else {
        await addDoc(collection(db, 'companies', companyId, 'shifts'), newShift);
        await logAuditEvent(companyId, AUDIT_ACTIONS.SHIFT_BLOCK_CREATED, {
          performedBy: user.uid,
          performedByName: userData?.displayName,
          targetType: 'shift',
          newValue: newShift,
        });
      }

      setShowCreateShift(false);
      setEditingShift(null);
    } catch (error) {
      console.error('Error creating shift:', error);
      alert('Failed to create shift');
    }
  };

  const deleteShift = async (shiftId) => {
    if (!window.confirm(t('shift.confirmDelete', 'Delete this shift?'))) return;
    try {
      await deleteDoc(doc(db, 'companies', companyId, 'shifts', shiftId));
      await logAuditEvent(companyId, AUDIT_ACTIONS.SHIFT_DELETED, {
        performedBy: user.uid,
        performedByName: userData?.displayName,
        targetId: shiftId,
        targetType: 'shift',
      });
    } catch (error) {
      console.error('Error deleting shift:', error);
    }
  };

  const handleOverrideConfirm = async (reason) => {
    if (!overrideModal) return;
    // Proceed with the blocked action
    const { shiftData, action } = overrideModal;
    setOverrideModal(null);
    if (action === 'create') {
      const newShift = {
        ...shiftData,
        complianceOverride: { reason, by: user.uid, at: new Date().toISOString() },
      };
      if (editingShift?.id) {
        await updateDoc(doc(db, 'companies', companyId, 'shifts', editingShift.id), {
          ...newShift,
          updatedAt: new Date().toISOString(),
        });
      } else {
        await addDoc(collection(db, 'companies', companyId, 'shifts'), newShift);
      }
      setShowCreateShift(false);
      setEditingShift(null);
    } else if (action === 'publish') {
      await doPublishRota(reason);
    }
  };

  const publishRota = async () => {
    if (publishableShifts.length === 0) return;
    setShowPublishReview(true);
  };

  const confirmPublishRota = async () => {
    // Check fatigue warnings before publishing
    const warnings = getRotaFatigueWarnings(shifts, staff);
    if (warnings.length > 0) {
      setShowPublishReview(false);
      setFatigueWarnings(warnings);
      setOverrideModal({
        errors: [],
        warnings: warnings.map(w => `${w.staffName}: Fatigue score ${w.score}/100 (${w.level})`),
        fatigueData: { score: warnings[0].score, level: warnings[0].level, recommendations: warnings[0].recommendations },
        action: 'publish',
      });
      return;
    }

    if (!window.confirm(t('rota.confirmPublish'))) return;
    setShowPublishReview(false);
    await doPublishRota();
  };

  const doPublishRota = async (overrideReason = null) => {
    try {
      if (rota) {
        await updateDoc(doc(db, 'companies', companyId, 'rotas', rota.id), {
          status: 'published',
          publishedAt: new Date().toISOString(),
          publishedBy: user.uid,
        });
      } else {
        await addDoc(collection(db, 'companies', companyId, 'rotas'), {
          weekStart,
          status: 'published',
          companyId,
          publishedAt: new Date().toISOString(),
          publishedBy: user.uid,
          createdAt: new Date().toISOString(),
        });
      }

      // Notify all assigned staff
      const assignedStaff = [...new Set(accessibleShifts.filter(s => s.assignedStaffId).map(s => s.assignedStaffId))];
      for (const staffId of assignedStaff) {
        await sendNotification(companyId, staffId, NOTIFICATION_TYPES.ROTA_PUBLISHED, {
          title: 'Rota Published',
          message: `The rota for week of ${weekStart} has been published.`,
        });
      }

      await logAuditEvent(companyId, AUDIT_ACTIONS.ROTA_PUBLISHED, {
        performedBy: user.uid,
        performedByName: userData?.displayName,
        targetType: 'rota',
        metadata: { weekStart, overrideReason: overrideReason || null },
      });

      alert(t('rota.publishSuccess'));
    } catch (error) {
      console.error('Error publishing rota:', error);
    }
  };

  // Cost projection (computed)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const rotaCost = useMemo(() => {
    if (!payRules || shifts.length === 0) return null;
    try { return projectRotaCost(shifts, staff, payRules); }
    catch { return null; }
  }, [shifts, staff, payRules]);

  const getAISuggestions = async (shift) => {
    const profiles = {};
    staff.forEach(s => { profiles[s.id] = s; });
    const suggestions = generateEnhancedSuggestions(shift, staff, shifts, {}, profiles, config);
    setAiSuggestions(prev => ({ ...prev, [shift.id]: suggestions }));
  };

  const assignStaffToShift = async (shiftId, staffId) => {
    try {
      const member = staff.find(s => s.assignmentId === staffId || s.id === staffId);
      const nextStaffId = getStaffAssignmentId(member || { id: staffId });
      const nextStaffName = resolveStaffDisplayName({}, member);
      await updateDoc(doc(db, 'companies', companyId, 'shifts', shiftId), {
        assignedStaffId: nextStaffId,
        assignedStaffName: nextStaffName,
        assignedTo: nextStaffId,
        assignedToName: nextStaffName,
        status: 'assigned',
        updatedAt: new Date().toISOString(),
      });
      await logAuditEvent(companyId, AUDIT_ACTIONS.SHIFT_UPDATED || 'shift_updated', {
        performedBy: user.uid,
        performedByName: userData?.displayName,
        targetType: 'shift',
        metadata: { shiftId, assignedTo: nextStaffId },
      });
      // Clear AI suggestions for this shift
      setAiSuggestions(prev => {
        const copy = { ...prev };
        delete copy[shiftId];
        return copy;
      });
    } catch (error) {
      console.error('Error assigning staff:', error);
      alert('Failed to assign staff');
    }
  };

  const unassignStaff = async (shiftId) => {
    try {
      await updateDoc(doc(db, 'companies', companyId, 'shifts', shiftId), {
        assignedStaffId: null,
        assignedStaffName: '',
        assignedTo: null,
        assignedToName: '',
        status: 'unassigned',
        updatedAt: new Date().toISOString(),
      });
    } catch (error) {
      console.error('Error unassigning staff:', error);
    }
  };

  // ── Smart Rota Builder ────────────────────────────────────────────────
  const runSmartRotaBuilder = async () => {
    setSmartRotaLoading(true);
    try {
      // Fetch last 8 weeks of historic shifts
      const eightWeeksAgo = new Date();
      eightWeeksAgo.setDate(eightWeeksAgo.getDate() - 56);
      const historicQ = query(
        collection(db, 'companies', companyId, 'shifts'),
        where('date', '>=', eightWeeksAgo.toISOString().split('T')[0]),
        where('date', '<', weekStart),
        orderBy('date'),
      );
      const historicSnap = await getDocs(historicQ);
      const historicShifts = historicSnap.docs.map(d => ({ id: d.id, ...d.data() }));

      // Fetch availability
      const availSnap = await getDocs(collection(db, 'companies', companyId, 'availability'));
      const availability = availSnap.docs.map(d => ({ id: d.id, ...d.data() }));

      // Build staff profiles map
      const profiles = {};
      staff.forEach(s => { profiles[s.id] = s; });

      // Analyse patterns
      const patterns = analysePatterns(historicShifts, 8);

      // Generate drafts for the current week
      const drafts = generateSmartRota(
        weekStart, patterns, staff, shifts, availability, historicShifts, profiles, config
      );

      const summary = summariseSmartRota(drafts);
      setSmartRotaDrafts(drafts);
      setSmartRotaSummary(summary);
      setSmartRotaOpen(true);
    } catch (err) {
      console.error('Smart Rota Builder error:', err);
      alert('Failed to generate smart rota. Please try again.');
    } finally {
      setSmartRotaLoading(false);
    }
  };

  const acceptSmartRota = async () => {
    if (!smartRotaDrafts) return;
    try {
      for (const draft of smartRotaDrafts) {
        const { _pattern, _confidence, _score, _reason, ...shiftData } = draft;
        const cleanShiftData = Object.fromEntries(
          Object.entries({
            ...shiftData,
            companyId,
            rotaId: rota?.id || null,
            createdBy: user.uid,
            smartGenerated: true,
          }).filter(([, value]) => value !== undefined)
        );
        await addDoc(collection(db, 'companies', companyId, 'shifts'), {
          ...cleanShiftData,
        });
      }
      await logAuditEvent(companyId, AUDIT_ACTIONS.SHIFT_BLOCK_CREATED || 'shift_created', {
        performedBy: user.uid,
        performedByName: userData?.displayName,
        targetType: 'smart_rota',
        metadata: { weekStart, draftsCount: smartRotaDrafts.length },
      });
      setSmartRotaOpen(false);
      setSmartRotaDrafts(null);
      setSmartRotaSummary(null);
    } catch (err) {
      console.error('Error saving smart rota:', err);
      alert('Failed to save shifts. Please try again.');
    }
  };

  const removeSmartDraft = (index) => {
    setSmartRotaDrafts(prev => {
      const updated = prev.filter((_, i) => i !== index);
      setSmartRotaSummary(summariseSmartRota(updated));
      return updated;
    });
  };
  // ── End Smart Rota Builder ────────────────────────────────────────────

  const isPublished = rota?.status === 'published';
  const lastShiftChangeAt = useMemo(() => {
    if (shifts.length === 0) return null;

    return shifts.reduce((latest, shift) => {
      const candidate = shift.updatedAt || shift.createdAt || null;
      if (!candidate) return latest;
      if (!latest) return candidate;
      return String(candidate).localeCompare(String(latest)) > 0 ? candidate : latest;
    }, null);
  }, [shifts]);

  const hasUnpublishedChanges = useMemo(() => {
    if (!isPublished) return false;
    if (!lastShiftChangeAt) return false;
    if (!rota?.publishedAt) return true;
    return String(lastShiftChangeAt).localeCompare(String(rota.publishedAt)) > 0;
  }, [isPublished, lastShiftChangeAt, rota?.publishedAt]);

  const canEdit = isManager(userData);

  // Filter shifts by selected group
  const filteredShifts = selectedGroup === 'all'
    ? accessibleShifts
    : accessibleShifts.filter(s => s.groupId === selectedGroup);

  const publishableShifts = useMemo(() => {
    return accessibleShifts.filter((shift) => shift.status !== 'published' && shift.status !== 'cancelled');
  }, [accessibleShifts]);

  const publishReviewRows = useMemo(() => {
    const byStaff = {};

    publishableShifts.forEach((shift) => {
      const staffId = shift.assignedStaffId;
      if (!staffId) return;

      if (!byStaff[staffId]) {
        const staffMember = staffByAssignmentId[staffId] || {};
        byStaff[staffId] = {
          staffId,
          name: resolveStaffDisplayName(shift, staffMember) || staffId,
          payrollId: staffMember.payrollId || '',
          workingHours: 0,
          totalHours: 0,
          sleepInCount: 0,
          shifts: 0,
        };
      }

      const metrics = calculateShiftSummaryMetrics(shift);
      byStaff[staffId].workingHours += metrics.workingHours;
      byStaff[staffId].totalHours += metrics.totalHours;
      byStaff[staffId].sleepInCount += metrics.sleepInCount;
      byStaff[staffId].shifts += 1;
    });

    return Object.values(byStaff).sort((a, b) => a.name.localeCompare(b.name));
  }, [publishableShifts, staffByAssignmentId]);

  const exportPublishReview = () => {
    if (publishReviewRows.length === 0) return;

    const header = ['Employee Name', 'Payroll / Staff ID', 'Shifts', 'Working Hours', 'Total Hours', 'Sleep-ins'];
    const rows = publishReviewRows.map((row) => [
      row.name,
      row.payrollId || '',
      row.shifts,
      row.workingHours.toFixed(1),
      row.totalHours.toFixed(1),
      row.sleepInCount,
    ]);
    const csvContent = [header, ...rows]
      .map((cols) => cols.map((value) => `"${String(value).replace(/"/g, '""')}"`).join(','))
      .join('\n');

    const blob = new Blob([`\uFEFF${csvContent}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `BuildMyShifts-RotaReview-${weekStart}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <Calendar size={24} className="text-primary" />
              {t('rota.title')}
            </h1>
            <p className="text-gray-400 mt-1">{t('rota.weekOf', { date: weekStart })}</p>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            {/* View range selector */}
            <select
              value={viewRange}
              onChange={e => setViewRange(e.target.value)}
              className="bg-white/5 border border-white/20 rounded-lg px-3 py-1.5 text-sm text-white focus:ring-2 focus:ring-primary/50 focus:border-primary [color-scheme:dark]"
            >
              {VIEW_RANGES.map(r => (
                <option key={r.key} value={r.key} className="bg-slate-700 text-white">{t(r.labelKey)}</option>
              ))}
            </select>

            {/* Navigation */}
            <div className="flex items-center gap-2">
              <button onClick={() => navigateView(-1)} className="p-2 rounded-lg hover:bg-white/10">
                <ChevronLeft size={20} />
              </button>
              <button
                onClick={() => setWeekStart(getWeekStart(new Date().toISOString().split('T')[0], weekStartDay))}
                className="px-3 py-1.5 text-sm font-medium text-primary border border-primary rounded-lg hover:bg-primary/5"
              >
                {t('common.today')}
              </button>
              <button onClick={() => navigateView(1)} className="p-2 rounded-lg hover:bg-white/10">
                <ChevronRight size={20} />
              </button>
            </div>

            {/* Rota status badge */}
            {isPublished ? (
              <span className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium ${
                hasUnpublishedChanges
                  ? 'bg-amber-100 text-amber-700'
                  : 'bg-green-100 text-green-700'
              }`}>
                <Eye size={14} /> {hasUnpublishedChanges ? t('rota.changesPending', 'Changes pending') : t('rota.published')}
              </span>
            ) : (
              <span className="flex items-center gap-1 px-3 py-1.5 bg-yellow-100 text-yellow-700 rounded-full text-sm font-medium">
                <EyeOff size={14} /> {t('rota.draft')}
              </span>
            )}

            {/* Actions */}
            {canEdit && (
              <>
                <button
                  onClick={() => { setEditingShift(null); setShowCreateShift(true); }}
                  className="flex items-center gap-1.5 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium"
                >
                  <Plus size={16} /> {t('shift.create')}
                </button>
                <button
                  onClick={runSmartRotaBuilder}
                  disabled={smartRotaLoading}
                  className="flex items-center gap-1.5 px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg hover:from-purple-700 hover:to-indigo-700 transition-all text-sm font-medium disabled:opacity-50"
                >
                  {smartRotaLoading ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
                  {t('smartRota.build', 'Smart Build')}
                </button>
                {rotaCost && (
                  <button onClick={() => setShowCostPanel(!showCostPanel)}
                    className="flex items-center gap-1.5 px-3 py-2 bg-white/10 text-gray-300 rounded-lg hover:bg-white/15 text-sm">
                    <DollarSign size={14} />
                    <span className="text-green-400 font-medium">{rotaCost.currency || '£'}{rotaCost.totalCost?.toFixed(0)}</span>
                  </button>
                )}
                {(!isPublished || hasUnpublishedChanges) && shifts.length > 0 && (
                  <button
                    onClick={publishRota}
                    className="flex items-center gap-1.5 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
                  >
                    <Send size={16} /> {hasUnpublishedChanges ? t('rota.republish', 'Republish Rota') : t('rota.publish')}
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Group/Location filter bar */}
      {groups.length > 0 && (
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4">
          <div className="flex items-center gap-3 flex-wrap">
            <Building2 size={18} className="text-gray-400" />
            <button
              onClick={() => setSelectedGroup('all')}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                selectedGroup === 'all'
                  ? 'bg-primary text-white'
                  : 'bg-white/10 text-gray-300 hover:bg-white/20'
              }`}
            >
              {t('groups.allLocations')}
            </button>
            {accessibleGroups.map(g => (
              <button
                key={g.id}
                onClick={() => setSelectedGroup(g.id)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors flex items-center gap-1.5 ${
                  selectedGroup === g.id
                    ? 'text-white'
                    : 'bg-white/10 text-gray-300 hover:bg-white/20'
                }`}
                style={selectedGroup === g.id ? { backgroundColor: g.color || '#2563EB' } : {}}
              >
                <span
                  className="w-2.5 h-2.5 rounded-full inline-block"
                  style={{ backgroundColor: g.color || '#2563EB' }}
                />
                {g.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Coverage gaps warning */}
      {coverageGaps.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
          <h3 className="font-semibold text-yellow-800 flex items-center gap-2">
            <AlertTriangle size={18} /> {t('rota.coverageGaps')}
          </h3>
          <div className="mt-2 space-y-1">
            {coverageGaps.slice(0, 3).map((gap, idx) => (
              <p key={idx} className="text-sm text-yellow-700">
                {gap.dayName} {gap.timeBlock} — {gap.shortfall} staff short (need {gap.expectedStaff}, have {gap.currentStaff})
              </p>
            ))}
          </div>
        </div>
      )}

      {showPublishReview && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="w-full max-w-4xl bg-slate-900 border border-white/10 rounded-2xl shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
              <div>
                <h2 className="text-xl font-bold text-white">{t('rota.publishReviewTitle', 'Review Before Publish')}</h2>
                <p className="text-sm text-gray-400 mt-1">
                  {t('rota.publishReviewSubtitle', 'Check working hours, total hours, and sleep-ins for assigned staff before publishing.')}
                </p>
              </div>
              <button onClick={() => setShowPublishReview(false)} className="text-gray-400 hover:text-white">
                <XIcon size={20} />
              </button>
            </div>

            <div className="px-6 py-4 border-b border-white/10 flex flex-wrap gap-4 text-sm">
              <div className="bg-white/5 rounded-lg px-4 py-3">
                <div className="text-gray-400">{t('rota.assignedStaff', 'Assigned Staff')}</div>
                <div className="text-white text-lg font-semibold">{publishReviewRows.length}</div>
              </div>
              <div className="bg-white/5 rounded-lg px-4 py-3">
                <div className="text-gray-400">{t('rota.totalWorkingHours', 'Working Hours')}</div>
                <div className="text-white text-lg font-semibold">
                  {publishReviewRows.reduce((sum, row) => sum + row.workingHours, 0).toFixed(1)}h
                </div>
              </div>
              <div className="bg-white/5 rounded-lg px-4 py-3">
                <div className="text-gray-400">{t('rota.totalHoursLabel', 'Total Hours')}</div>
                <div className="text-white text-lg font-semibold">
                  {publishReviewRows.reduce((sum, row) => sum + row.totalHours, 0).toFixed(1)}h
                </div>
              </div>
              <div className="bg-white/5 rounded-lg px-4 py-3">
                <div className="text-gray-400">{t('rota.sleepInCountLabel', 'Sleep-ins')}</div>
                <div className="text-white text-lg font-semibold">
                  {publishReviewRows.reduce((sum, row) => sum + row.sleepInCount, 0)}
                </div>
              </div>
            </div>

            <div className="max-h-[55vh] overflow-auto">
              {publishReviewRows.length === 0 ? (
                <div className="px-6 py-10 text-center text-gray-400">
                  {t('rota.noAssignedStaffToReview', 'No assigned staff to review for this publish.')}
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-white/5 sticky top-0">
                    <tr className="text-left text-gray-400">
                      <th className="px-6 py-3">{t('rota.staffMember', 'Staff')}</th>
                      <th className="px-4 py-3">{t('rota.payrollId', 'Payroll / Staff ID')}</th>
                      <th className="px-4 py-3 text-right">{t('rota.shiftsCount', 'Shifts')}</th>
                      <th className="px-4 py-3 text-right">{t('rota.workingHours', 'Working')}</th>
                      <th className="px-4 py-3 text-right">{t('rota.totalHours', 'Total')}</th>
                      <th className="px-6 py-3 text-right">{t('rota.sleepIns', 'Sleep-ins')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {publishReviewRows.map((row) => (
                      <tr key={row.staffId} className="border-t border-white/5">
                        <td className="px-6 py-3 text-white">{row.name}</td>
                        <td className="px-4 py-3 text-gray-300">{row.payrollId || '—'}</td>
                        <td className="px-4 py-3 text-right text-gray-300">{row.shifts}</td>
                        <td className="px-4 py-3 text-right text-white">{row.workingHours.toFixed(1)}h</td>
                        <td className="px-4 py-3 text-right text-white">{row.totalHours.toFixed(1)}h</td>
                        <td className="px-6 py-3 text-right text-gray-300">{row.sleepInCount}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="px-6 py-4 border-t border-white/10 flex items-center justify-between gap-3">
              <button
                onClick={exportPublishReview}
                disabled={publishReviewRows.length === 0}
                className="px-4 py-2 rounded-lg bg-white/10 text-white hover:bg-white/15 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('rota.exportReview', 'Export Review CSV')}
              </button>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setShowPublishReview(false)}
                  className="px-4 py-2 rounded-lg text-gray-300 hover:text-white"
                >
                  {t('common.cancel', 'Cancel')}
                </button>
                <button
                  onClick={confirmPublishRota}
                  className="px-4 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700"
                >
                  {hasUnpublishedChanges ? t('rota.republish', 'Republish Rota') : t('rota.publish', 'Publish Rota')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Weekly grid — repeated per week chunk */}
      {loading ? (
        <LoadingSpinner />
      ) : (
        <div className="space-y-4">
          {weekChunks.map((weekDts, wIdx) => {
            const weekLabel = parseDisplayDate(weekDts[0]).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
              + ' – ' + new Date(weekDts[weekDts.length - 1]).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
            const safeWeekLabel = parseDisplayDate(weekDts[0]).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
              + ' - ' + parseDisplayDate(weekDts[weekDts.length - 1]).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
            return (
              <div key={wIdx}>
                {/* Week label for multi-week views */}
                {viewRange !== '1w' && (
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1 pl-1">
                    {safeWeekLabel}
                  </p>
                )}
                <div className="grid grid-cols-1 lg:grid-cols-7 gap-2">
                  {weekDts.map((date, idx) => {
                    const dayShifts = filteredShifts.filter(s => s.date === date);
                    const isToday = date === new Date().toISOString().split('T')[0];
                    const getShiftLocationColor = (shift) => shift.groupColor || accessibleGroupsById[shift.groupId]?.color || null;
                    const selectedGroupColor = selectedGroup !== 'all'
                      ? accessibleGroups.find((group) => group.id === selectedGroup)?.color || null
                      : null;
                    const dayThemeColor = selectedGroupColor || getDominantDayColor(dayShifts, getShiftLocationColor);
                    const dayCardStyle = dayThemeColor
                      ? {
                          borderColor: hexToRgba(dayThemeColor, isToday ? 0.7 : 0.28),
                          boxShadow: isToday
                            ? `0 0 0 1px ${hexToRgba(dayThemeColor, 0.5)}, 0 0 18px ${hexToRgba(dayThemeColor, 0.2)}`
                            : `0 0 0 1px ${hexToRgba(dayThemeColor, 0.12)}`,
                        }
                      : {};
                    const dayHeaderStyle = isToday && dayThemeColor
                      ? {
                          background: `linear-gradient(180deg, ${hexToRgba(dayThemeColor, 0.95)} 0%, ${hexToRgba(dayThemeColor, 0.82)} 100%)`,
                        }
                      : {};
                    const noShiftsStyle = dayThemeColor
                      ? { color: hexToRgba(dayThemeColor, 0.85) }
                      : {};
                    const addShiftButtonStyle = dayThemeColor
                      ? {
                          borderColor: hexToRgba(dayThemeColor, 0.28),
                          color: hexToRgba(dayThemeColor, 0.98),
                          backgroundColor: hexToRgba(dayThemeColor, 0.05),
                        }
                      : {};

                    return (
                      <div
                        key={date}
                        className={`bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl overflow-hidden ${isToday && !dayThemeColor ? 'ring-2 ring-primary' : ''}`}
                        style={dayCardStyle}
                      >
                        {/* Day header */}
                        <div
                          className={`px-3 py-2 text-center ${isToday ? 'bg-primary text-white' : 'bg-white/5'}`}
                          style={dayHeaderStyle}
                        >
                          <p className="text-xs font-medium uppercase">{dayNameForDate(date)}</p>
                          <p className={`text-lg font-bold ${isToday ? 'text-white' : 'text-white'}`}>
                            {parseDisplayDate(date).getDate()}
                          </p>
                          {(viewRange !== '1w') && (
                            <p className="text-[10px] text-gray-400">
                              {parseDisplayDate(date).toLocaleDateString(undefined, { month: 'short' })}
                            </p>
                          )}
                        </div>

                {/* Shifts */}
                <div className="p-2 space-y-2 min-h-[120px]">
                  {dayShifts.length === 0 ? (
                    <div className="text-center py-4 text-gray-300 text-xs" style={noShiftsStyle}>{t('rota.noShifts')}</div>
                  ) : (
                    dayShifts.map(shift => {
                      const hasSegments = shift.segments?.length > 0;
                      const assignedMember = shift.assignedStaffId ? staffByAssignmentId[shift.assignedStaffId] : null;
                      const assignedName = resolveStaffDisplayName(shift, assignedMember);
                      const segColors = {
                        [SEGMENT_TYPES.STANDARD]: 'bg-blue-500',
                        [SEGMENT_TYPES.SLEEP_IN]: 'bg-indigo-500',
                        [SEGMENT_TYPES.ON_CALL]: 'bg-amber-500',
                        [SEGMENT_TYPES.BREAK]: 'bg-gray-400',
                      };
                      const isAllLocationsView = selectedGroup === 'all';
                      const locationTint = getShiftLocationColor(shift);
                      const locationAccentStyle = locationTint
                        ? {
                            borderColor: hexToRgba(locationTint, 0.5),
                            background: shift.assignedStaffId
                              ? `linear-gradient(180deg, ${hexToRgba(locationTint, 0.26)} 0%, ${hexToRgba(locationTint, 0.16)} 100%)`
                              : `linear-gradient(180deg, ${hexToRgba(locationTint, 0.14)} 0%, rgba(255,255,255,0.03) 100%)`,
                            boxShadow: isAllLocationsView
                              ? `inset 4px 0 0 ${locationTint}, 0 0 0 1px ${hexToRgba(locationTint, 0.14)}`
                              : `0 0 0 1px ${hexToRgba(locationTint, 0.14)}`,
                          }
                        : {};

                      return (
                      <div
                        key={shift.id}
                        className={`p-2 rounded-lg text-xs border cursor-pointer hover:shadow-md transition-shadow ${
                          shift.assignedStaffId
                            ? shift.status === 'confirmed'
                              ? 'bg-green-900/40 border-green-500/30'
                              : 'bg-blue-900/40 border-blue-500/30'
                            : 'bg-white/5 border-white/10 border-dashed'
                        }`}
                        style={locationAccentStyle}
                        onClick={() => canEdit && getAISuggestions(shift)}
                      >
                        {/* Segment colour bar */}
                        {hasSegments && (
                          <div className="flex h-1.5 rounded overflow-hidden mb-1.5 bg-gray-900/50">
                            {shift.segments.map((seg, si) => (
                              <div key={si} className={`${segColors[seg.type] || 'bg-gray-500'} h-full flex-1`}
                                title={`${seg.type}: ${seg.startTime}–${seg.endTime}`} />
                            ))}
                          </div>
                        )}

                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-white">
                              {shift.startTime}–{shift.endTime}
                            </p>
                            <div className="flex items-center gap-2 min-w-0">
                              {assignedMember?.photoURL ? (
                                <img
                                  src={assignedMember.photoURL}
                                  alt={assignedName || 'Staff'}
                                  className="w-5 h-5 rounded-full object-cover border border-white/10 flex-shrink-0"
                                />
                              ) : (
                                <div className="w-5 h-5 rounded-full bg-primary/10 text-primary flex items-center justify-center text-[10px] font-bold flex-shrink-0">
                                  {(assignedName || '?').charAt(0).toUpperCase()}
                                </div>
                              )}
                              <p className="text-gray-400 truncate">
                                {assignedName || t('shift.unassigned')}
                              </p>
                            </div>
                          </div>
                          {/* Edit/Delete for manager */}
                          {canEdit && (
                            <div className="flex gap-0.5 ml-1">
                              <button onClick={e => { e.stopPropagation(); setEditingShift(shift); setShowCreateShift(true); }}
                                className="p-0.5 text-gray-500 hover:text-blue-400"><Edit3 size={10} /></button>
                              <button onClick={e => { e.stopPropagation(); deleteShift(shift.id); }}
                                className="p-0.5 text-gray-500 hover:text-red-400"><Trash2 size={10} /></button>
                            </div>
                          )}
                        </div>

                        {/* Segment type badges */}
                        {hasSegments && shift.segments.some(s => s.type !== SEGMENT_TYPES.STANDARD) && (
                          <div className="flex flex-wrap gap-0.5 mt-1">
                            {[...new Set(shift.segments.filter(s => s.type !== SEGMENT_TYPES.STANDARD).map(s => s.type))].map(type => (
                              <span key={type} className={`inline-block px-1 py-0.5 rounded text-[9px] text-white ${segColors[type]}`}>
                                {type === SEGMENT_TYPES.SLEEP_IN ? `🛏 ${t('rota.sleep')}` : type === SEGMENT_TYPES.ON_CALL ? `📞 ${t('rota.onCall')}` : type === SEGMENT_TYPES.BREAK ? `☕ ${t('rota.break')}` : type}
                              </span>
                            ))}
                          </div>
                        )}

                        {shift.blockType && shift.blockType !== BLOCK_TYPES.SINGLE && (
                          <span className="inline-block px-1 py-0.5 mt-0.5 rounded text-[9px] bg-purple-600/60 text-purple-200">
                            {shift.blockType === BLOCK_TYPES.LINKED ? `🔗 ${t('rota.linked')}` : `✂️ ${t('rota.split')}`}
                          </span>
                        )}

                        {shift.groupName && (
                          <p
                            className={`truncate text-[10px] flex items-center gap-1 mt-1 rounded px-1.5 py-0.5 ${isAllLocationsView ? 'text-white bg-white/5' : 'text-gray-500'}`}
                            style={isAllLocationsView && locationTint ? { border: `1px solid ${locationTint}66` } : {}}
                          >
                            <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: locationTint || '#2563EB' }} />
                            {shift.groupName}
                          </p>
                        )}
                        {shift.requiredRole && (
                          <p className="text-gray-400 truncate">{shift.requiredRole}</p>
                        )}
                        {shift.complianceOverride && (
                          <span className="inline-block mt-0.5 px-1 py-0.5 rounded text-[9px] bg-red-900/40 text-red-300 border border-red-700/30">
                            ⚠️ {t('rota.override')}
                          </span>
                        )}

                        {/* Quick-assign dropdown for unassigned shifts */}
                        {!shift.assignedStaffId && canEdit && (
                          <select
                            className="mt-1.5 w-full bg-slate-700 border border-white/20 rounded px-1.5 py-1 text-[11px] text-white"
                            value=""
                            onClick={e => e.stopPropagation()}
                            onChange={e => {
                              if (e.target.value) assignStaffToShift(shift.id, e.target.value);
                            }}
                          >
                            <option value="" className="bg-slate-700 text-gray-300">
                              {t('rota.assignToStaff', 'Assign to staff...')}
                            </option>
                            {staff
                              .filter((s) => (s.displayName || s.username || s.email || '').trim())
                              .map(s => (
                              <option key={s.id} value={s.assignmentId || s.id} className="bg-slate-700 text-white">
                                {s.displayName || s.username || s.email}
                              </option>
                            ))}
                          </select>
                        )}

                        {/* Unassign button for assigned shifts */}
                        {shift.assignedStaffId && canEdit && (
                          <button
                            className="mt-1 flex items-center gap-1 text-red-400 hover:text-red-300 text-[10px]"
                            onClick={e => { e.stopPropagation(); unassignStaff(shift.id); }}
                          >
                            <XIcon size={10} /> {t('shift.unassign')}
                          </button>
                        )}

                        {!shift.assignedStaffId && canEdit && (
                          <button
                            className="mt-1 flex items-center gap-1 text-primary hover:text-primary-dark"
                            onClick={(e) => { e.stopPropagation(); getAISuggestions(shift); }}
                          >
                            <Zap size={10} /> {t('ai.aiFill')}
                          </button>
                        )}
                      </div>
                    );
                    })
                  )}

                  {/* Add shift button for this day */}
                  {canEdit && (
                    <button
                      onClick={() => { setSelectedDay(date); setShowCreateShift(true); }}
                      className="w-full py-1.5 border-2 border-dashed border-white/10 rounded-lg text-gray-400 hover:text-primary hover:border-primary text-xs transition-colors"
                      style={addShiftButtonStyle}
                    >
                      {t('rota.addShift')}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* AI Suggestions panel */}
      {Object.keys(aiSuggestions).length > 0 && (
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2 mb-4">
            <Zap size={20} className="text-primary" />
            {t('ai.autoFill')}
          </h3>
          {Object.entries(aiSuggestions).map(([shiftId, suggestions]) => (
            <div key={shiftId} className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-gray-300">{t('ai.shiftLabel')}: {shifts.find(s => s.id === shiftId)?.startTime}–{shifts.find(s => s.id === shiftId)?.endTime} ({shifts.find(s => s.id === shiftId)?.date})</p>
                <button
                  onClick={() => setAiSuggestions(prev => { const c = {...prev}; delete c[shiftId]; return c; })}
                  className="p-1 hover:bg-white/10 rounded text-gray-400"
                >
                  <XIcon size={14} />
                </button>
              </div>
              <div className="space-y-2">
                {suggestions.filter(s => s.available).slice(0, 5).map((sug, idx) => (
                  <div key={idx} className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer hover:bg-white/5 transition-colors ${
                    sug.confidence === 'high' ? 'confidence-high' : sug.confidence === 'medium' ? 'confidence-medium' : 'confidence-low'
                  }`}
                    onClick={() => assignStaffToShift(shiftId, sug.staffId)}
                  >
                    <div>
                      <p className="font-medium text-white">{sug.staffName}</p>
                      <p className="text-xs mt-0.5 text-gray-400">{sug.reasonList?.join(' · ') || t('ai.noReasons')}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <p className="text-lg font-bold text-white">{sug.score}</p>
                        <p className="text-xs capitalize text-gray-400">{sug.confidence}</p>
                      </div>
                      <button
                        className="flex items-center gap-1 px-2 py-1 bg-primary/20 text-primary rounded text-xs font-medium hover:bg-primary/30"
                        onClick={e => { e.stopPropagation(); assignStaffToShift(shiftId, sug.staffId); }}
                      >
                        <UserPlus size={12} /> {t('shift.assign')}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Cost Projection Panel */}
      {showCostPanel && rotaCost && (
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <DollarSign size={20} className="text-green-400" />
              {t('cost.projectedCost', 'Projected Rota Cost')}
            </h3>
            <button onClick={() => setShowCostPanel(false)} className="p-1 hover:bg-white/10 rounded text-gray-400"><XIcon size={14} /></button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <div className="bg-gray-800 rounded-lg p-3 text-center">
              <p className="text-xs text-gray-400">{t('cost.total', 'Total')}</p>
              <p className="text-xl font-bold text-green-400">{rotaCost.currency || '£'}{rotaCost.totalCost?.toFixed(2)}</p>
            </div>
            <div className="bg-gray-800 rounded-lg p-3 text-center">
              <p className="text-xs text-gray-400">{t('cost.standard', 'Standard')}</p>
              <p className="text-lg font-semibold text-white">{rotaCost.currency || '£'}{rotaCost.breakdown?.standard?.toFixed(2) || '0'}</p>
            </div>
            <div className="bg-gray-800 rounded-lg p-3 text-center">
              <p className="text-xs text-gray-400">{t('cost.premium', 'Premium/OT')}</p>
              <p className="text-lg font-semibold text-amber-400">{rotaCost.currency || '£'}{((rotaCost.breakdown?.premium || 0) + (rotaCost.breakdown?.overtime || 0)).toFixed(2)}</p>
            </div>
            <div className="bg-gray-800 rounded-lg p-3 text-center">
              <p className="text-xs text-gray-400">{t('cost.sleepOnCall', 'Sleep/On-call')}</p>
              <p className="text-lg font-semibold text-indigo-400">{rotaCost.currency || '£'}{((rotaCost.breakdown?.sleepIn || 0) + (rotaCost.breakdown?.onCall || 0)).toFixed(2)}</p>
            </div>
          </div>
          {rotaCost.perStaff && Object.keys(rotaCost.perStaff).length > 0 && (
            <div className="text-xs text-gray-400">
              <p className="font-medium mb-1">{t('cost.perStaff', 'Per Staff:')}</p>
              <div className="flex flex-wrap gap-2">
                {Object.entries(rotaCost.perStaff).slice(0, 10).map(([sid, cost]) => (
                  <span key={sid} className="bg-gray-800 px-2 py-1 rounded">
                    {staff.find(s => s.id === sid)?.displayName || sid}: £{cost.toFixed(0)}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Smart Rota Builder Preview Modal */}
      {smartRotaOpen && smartRotaDrafts && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={() => setSmartRotaOpen(false)}>
          <div className="bg-slate-900 border border-white/10 rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 bg-gradient-to-r from-purple-900/40 to-indigo-900/40">
              <div className="flex items-center gap-3">
                <Sparkles size={22} className="text-purple-400" />
                <div>
                  <h2 className="text-lg font-bold text-white">{t('smartRota.title', 'Smart Rota Builder')}</h2>
                  <p className="text-xs text-gray-400">{t('smartRota.subtitle', 'AI-generated rota based on your last 8 weeks')}</p>
                </div>
              </div>
              <button onClick={() => setSmartRotaOpen(false)} className="p-1.5 hover:bg-white/10 rounded-lg text-gray-400"><XIcon size={18} /></button>
            </div>

            {/* Summary stats */}
            {smartRotaSummary && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 px-6 py-4 border-b border-white/10">
                <div className="bg-white/5 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-white">{smartRotaSummary.total}</p>
                  <p className="text-xs text-gray-400">{t('smartRota.totalShifts', 'Shifts')}</p>
                </div>
                <div className="bg-white/5 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-green-400">{smartRotaSummary.assigned}</p>
                  <p className="text-xs text-gray-400">{t('smartRota.assigned', 'Assigned')}</p>
                </div>
                <div className="bg-white/5 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-yellow-400">{smartRotaSummary.unassigned}</p>
                  <p className="text-xs text-gray-400">{t('smartRota.unassigned', 'Unassigned')}</p>
                </div>
                <div className="bg-white/5 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-purple-400">{smartRotaSummary.highConf}</p>
                  <p className="text-xs text-gray-400">{t('smartRota.highConfidence', 'High Confidence')}</p>
                </div>
              </div>
            )}

            {/* Draft list */}
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-2">
              {smartRotaDrafts.length === 0 ? (
                <p className="text-center text-gray-400 py-8">{t('smartRota.noDrafts', 'No patterns found. Try building some rotas first so the AI can learn your schedule.')}</p>
              ) : (
                smartRotaDrafts.map((draft, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-3 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 transition-colors">
                    {/* Confidence indicator */}
                    <div className={`w-2 h-10 rounded-full flex-shrink-0 ${
                      draft._confidence === 'high' ? 'bg-green-500' :
                      draft._confidence === 'medium' ? 'bg-yellow-500' :
                      draft._confidence === 'low' ? 'bg-orange-500' : 'bg-red-500'
                    }`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold text-white">
                          {dayNameForDate(draft.date)} {parseDisplayDate(draft.date).getDate()}
                        </p>
                        <span className="text-xs text-gray-400">{draft.startTime}–{draft.endTime}</span>
                        {draft.groupName && (
                          <span className="text-xs px-1.5 py-0.5 rounded-full bg-white/10 text-gray-300">{draft.groupName}</span>
                        )}
                      </div>
                      <p className="text-xs text-gray-400 truncate">
                        {resolveStaffDisplayName(draft, staffByAssignmentId[draft.assignedStaffId]) || t('shift.unassigned')} · {draft._reason}
                      </p>
                    </div>
                    <button
                      onClick={() => removeSmartDraft(idx)}
                      className="p-1.5 hover:bg-red-500/20 rounded text-gray-500 hover:text-red-400 flex-shrink-0"
                      title={t('common.remove', 'Remove')}
                    >
                      <XIcon size={14} />
                    </button>
                  </div>
                ))
              )}
            </div>

            {/* Footer actions */}
            <div className="flex items-center justify-between px-6 py-4 border-t border-white/10 bg-white/5">
              <button
                onClick={() => setSmartRotaOpen(false)}
                className="px-4 py-2 text-sm text-gray-400 hover:text-white transition-colors"
              >
                {t('common.cancel', 'Cancel')}
              </button>
              <button
                onClick={acceptSmartRota}
                disabled={!smartRotaDrafts || smartRotaDrafts.length === 0}
                className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:from-green-700 hover:to-emerald-700 text-sm font-semibold disabled:opacity-50 transition-all"
              >
                <Check size={16} /> {t('smartRota.accept', 'Accept & Create Shifts')} ({smartRotaDrafts.length})
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Shift Block Modal */}
      {showCreateShift && (
        <ShiftBlockModal
          isOpen={showCreateShift}
          onClose={() => { setShowCreateShift(false); setEditingShift(null); setSelectedDay(null); }}
          onSave={createShift}
          existingShift={editingShift}
          date={selectedDay || weekDates[0]}
          staff={accessibleStaff}
          groups={groups}
          locations={accessibleGroups}
          payRules={payRules}
          sectorConfig={sectorConfig}
          templates={shiftTemplates}
        />
      )}

      {/* Compliance Override Modal */}
      {overrideModal && (
        <ComplianceOverrideModal
          isOpen={!!overrideModal}
          onClose={() => setOverrideModal(null)}
          onConfirm={handleOverrideConfirm}
          companyId={companyId}
          userId={user.uid}
          userName={userData?.displayName}
          errors={overrideModal.errors}
          warnings={overrideModal.warnings}
          fatigueData={overrideModal.fatigueData}
          shiftId={overrideModal.shiftData?.id}
          rotaId={rota?.id}
          staffId={overrideModal.shiftData?.assignedStaffId}
        />
      )}
    </div>
  );
}


